/**
 * Scroll-to-Error Utilities
 *
 * Utility functions for implementing scroll-to-first-error functionality
 * in form components. Provides smooth scrolling and accessibility features.
 *
 * @module utils/scrollToError
 */

import { getLogger } from "@/services/shared/LoggerFactory";

/**
 * Configuration options for scroll-to-error behavior
 */
export interface ScrollToErrorOptions {
  /** Scroll behavior (default: 'smooth') */
  behavior?: ScrollBehavior;
  /** Block alignment (default: 'center') */
  block?: ScrollLogicalPosition;
  /** Inline alignment (default: 'nearest') */
  inline?: ScrollLogicalPosition;
  /** Delay before focusing element in ms (default: 300) */
  focusDelay?: number;
  /** Whether to focus the element after scrolling (default: true) */
  shouldFocus?: boolean;
}

/**
 * Default scroll-to-error options
 */
const DEFAULT_OPTIONS: Required<ScrollToErrorOptions> = {
  behavior: 'smooth',
  block: 'center',
  inline: 'nearest',
  focusDelay: 300,
  shouldFocus: true
};

/**
 * Scrolls to a specific element and optionally focuses it
 *
 * @param element - The HTML element to scroll to
 * @param options - Configuration options for scrolling behavior
 *
 * @example
 * ```typescript
 * const inputElement = document.getElementById('email-input');
 * if (inputElement) {
 *   scrollToElement(inputElement, {
 *     behavior: 'smooth',
 *     block: 'center',
 *     focusDelay: 200
 *   });
 * }
 * ```
 */
export const scrollToElement = (
  element: HTMLElement,
  options: ScrollToErrorOptions = {}
): void => {
  const config = { ...DEFAULT_OPTIONS, ...options };

  // Scroll to the element
  element.scrollIntoView({
    behavior: config.behavior,
    block: config.block,
    inline: config.inline
  });

  // Focus the element after a delay if requested
  if (config.shouldFocus) {
    setTimeout(() => {
      element.focus();
    }, config.focusDelay);
  }
};

/**
 * Finds the first field with validation errors from a list of field names
 *
 * @param fieldOrder - Array of field names in the order they should be checked
 * @param validationState - Validation state object containing field validation results
 * @returns The name of the first field with an error, or null if none found
 *
 * @example
 * ```typescript
 * const firstErrorField = findFirstErrorField(
 *   ['firstName', 'lastName', 'email'],
 *   {
 *     firstName: { touched: true, result: { isValid: true } },
 *     lastName: { touched: true, result: { isValid: false, error: 'Required' } },
 *     email: { touched: true, result: { isValid: false, error: 'Invalid' } }
 *   }
 * );
 * // Returns: 'lastName'
 * ```
 */
export const findFirstErrorField = (
  fieldOrder: string[],
  validationState: Record<string, {
    touched?: boolean;
    result: {
      isValid: boolean;
      error?: string;
    };
  }>
): string | null => {
  for (const fieldName of fieldOrder) {
    const validation = validationState[fieldName];
    if (validation?.touched && !validation.result.isValid) {
      return fieldName;
    }
  }
  return null;
};

/**
 * Scrolls to the first field with validation errors using refs
 *
 * @param fieldOrder - Array of field names in the order they should be checked
 * @param validationState - Validation state object containing field validation results
 * @param fieldRefs - React refs object containing references to form fields
 * @param options - Configuration options for scrolling behavior
 * @returns Whether a field was found and scrolled to
 *
 * @example
 * ```typescript
 * const scrolled = scrollToFirstErrorField(
 *   ['firstName', 'lastName', 'email'],
 *   validationState,
 *   fieldRefs.current,
 *   { behavior: 'smooth', focusDelay: 200 }
 * );
 *
 * if (scrolled) {
 *   logger.log('Scrolled to first error field');
 * }
 * ```
 */
export const scrollToFirstErrorField = (
  fieldOrder: string[],
  validationState: Record<string, {
    touched?: boolean;
    result: {
      isValid: boolean;
      error?: string;
    };
  }>,
  fieldRefs: Record<string, HTMLElement | null>,
  options: ScrollToErrorOptions = {}
): boolean => {
  const logger = getLogger();
  const firstErrorField = findFirstErrorField(fieldOrder, validationState);
  logger.log('firstErrorField: ', firstErrorField);
  if (firstErrorField && fieldRefs[firstErrorField]) {
    const element = fieldRefs[firstErrorField];
    if (element) {
      scrollToElement(element, options);
      return true;
    }
  }

  return false;
};

/**
 * Creates a scroll-to-error handler for form components
 *
 * @param fieldOrder - Array of field names in the order they should be checked
 * @param validationState - Validation state object containing field validation results
 * @param fieldRefs - React refs object containing references to form fields
 * @param options - Configuration options for scrolling behavior
 * @returns A function that can be called to scroll to the first error
 *
 * @example
 * ```typescript
 * const scrollToError = createScrollToErrorHandler(
 *   ['firstName', 'lastName', 'email'],
 *   validationState,
 *   fieldRefs.current
 * );
 *
 * // Later, when validation fails:
 * if (!isValid) {
 *   setTimeout(scrollToError, 100);
 * }
 * ```
 */
export const createScrollToErrorHandler = (
  fieldOrder: string[],
  validationState: Record<string, {
    touched?: boolean;
    result: {
      isValid: boolean;
      error?: string;
    };
  }>,
  fieldRefs: Record<string, HTMLElement | null>,
  options: ScrollToErrorOptions = {}
) => {
  return () => scrollToFirstErrorField(fieldOrder, validationState, fieldRefs, options);
};